import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { InheritableField } from './inheritable-field'
import {
  GEMINI_CATEGORIES,
  GEMINI_THRESHOLDS,
} from '@/schemas/workflow-node'
import {
  CAP_THINKING_ADAPTIVE,
  CAP_THINKING_MANUAL,
  CAP_REASONING,
  CAP_REASONING_MINIMAL,
  CAP_REASONING_NONE,
  CAP_SAFETY_SETTINGS,
} from '@/schemas/llm-models'

type Thinking = { enabled: boolean; budget_tokens?: number }
type ReasoningEffort = 'minimal' | 'low' | 'medium' | 'high' | 'none'
type SafetySettings = Partial<
  Record<(typeof GEMINI_CATEGORIES)[number], (typeof GEMINI_THRESHOLDS)[number]>
>

export interface ModelSpecificBlob {
  thinking?: Thinking
  reasoning_effort?: ReasoningEffort
  safety_settings?: SafetySettings
}

export interface ModelSpecificSectionProps {
  capabilities: string[]
  isCustomModel?: boolean
  value: ModelSpecificBlob
  maxTokens: number
  onChange: (next: ModelSpecificBlob) => void
  disabled?: boolean
}

/**
 * Phase 4 plan 04-05: conditional per-model-capability sub-forms.
 *
 * - thinking_manual / thinking_adaptive → thinking.enabled + budget_tokens
 * - reasoning / reasoning_minimal / reasoning_none → reasoning_effort select
 * - safety_settings → 4 Gemini category selects
 *
 * If `isCustomModel=true` (D-03) renders ALL three sub-forms unconditionally
 * with a warning banner (P-09 / llmConfig.custom.warning).
 */
export function ModelSpecificSection({
  capabilities,
  isCustomModel,
  value,
  maxTokens,
  onChange,
  disabled,
}: ModelSpecificSectionProps) {
  const { t } = useTranslation('workflows')

  const showThinking =
    isCustomModel ||
    capabilities.includes(CAP_THINKING_MANUAL) ||
    capabilities.includes(CAP_THINKING_ADAPTIVE)
  const showReasoning =
    isCustomModel ||
    capabilities.includes(CAP_REASONING) ||
    capabilities.includes(CAP_REASONING_MINIMAL) ||
    capabilities.includes(CAP_REASONING_NONE)
  const showSafety =
    isCustomModel || capabilities.includes(CAP_SAFETY_SETTINGS)

  if (!showThinking && !showReasoning && !showSafety) return null

  const thinking = value.thinking ?? { enabled: false }
  const reasoning = value.reasoning_effort ?? 'medium'
  const safety = value.safety_settings ?? {}

  const budgetExceedsMax =
    thinking.budget_tokens != null && thinking.budget_tokens >= maxTokens

  return (
    <section className="space-y-4">
      <h4 className="text-sm font-semibold text-gray-200">
        {t('llmConfig.modelSpecific.title')}
      </h4>

      {isCustomModel && (
        <p className="text-xs rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 px-3 py-2">
          {t('llmConfig.custom.warning')}
        </p>
      )}

      {showThinking && (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-300">
            {t('llmConfig.modelSpecific.thinking.label')}
          </Label>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={!!thinking.enabled}
              disabled={disabled}
              onCheckedChange={(checked) =>
                onChange({
                  ...value,
                  thinking: { ...thinking, enabled: !!checked },
                })
              }
            />
            <span className="text-sm text-gray-300">
              {t('llmConfig.modelSpecific.thinking.enabled')}
            </span>
          </div>
          {thinking.enabled && (
            <div className="space-y-1">
              <Label className="text-xs text-gray-400">
                {t('llmConfig.modelSpecific.thinking.budgetTokens')}
              </Label>
              <Input
                type="number"
                min={1}
                value={thinking.budget_tokens ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  onChange({
                    ...value,
                    thinking: {
                      ...thinking,
                      budget_tokens: Number.parseInt(e.target.value, 10) || 0,
                    },
                  })
                }
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              />
              {budgetExceedsMax && (
                <p className="text-xs text-amber-300">
                  {t('llmConfig.modelSpecific.thinking.budgetLessThanMaxWarning')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {showReasoning && (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-300">
            {t('llmConfig.modelSpecific.reasoningEffort.label')}
          </Label>
          <Select
            value={reasoning}
            disabled={disabled}
            onValueChange={(v) =>
              onChange({ ...value, reasoning_effort: v as ReasoningEffort })
            }
          >
            <SelectTrigger className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(['none', 'minimal', 'low', 'medium', 'high'] as const).map(
                (v) => (
                  <SelectItem key={v} value={v}>
                    {t(`llmConfig.modelSpecific.reasoningEffort.values.${v}`)}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {showSafety && (
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-gray-300">
            {t('llmConfig.modelSpecific.safetySettings.title')}
          </Label>
          <div className="space-y-2">
            {GEMINI_CATEGORIES.map((cat) => {
              const current = safety[cat] ?? 'block_medium_and_above'
              return (
                <div
                  key={cat}
                  className="grid grid-cols-2 gap-2 items-center"
                >
                  <span className="text-sm text-gray-300">
                    {t(
                      `llmConfig.modelSpecific.safetySettings.categories.${cat}`
                    )}
                  </span>
                  <InheritableField<(typeof GEMINI_THRESHOLDS)[number]>
                    label=""
                    inheritedValue={current}
                    value={safety[cat] ?? null}
                    inherited={safety[cat] == null}
                    onChange={(val, isInh) => {
                      const next: SafetySettings = { ...safety }
                      if (isInh) delete next[cat]
                      else next[cat] = val
                      onChange({ ...value, safety_settings: next })
                    }}
                    disabled={disabled}
                    formatDisplay={(v) =>
                      t(
                        `llmConfig.modelSpecific.safetySettings.thresholds.${v}`
                      )
                    }
                    renderInput={({ value: v, onChange: oc, disabled: d }) => (
                      <Select
                        value={v}
                        disabled={d}
                        onValueChange={(n) =>
                          oc(n as (typeof GEMINI_THRESHOLDS)[number])
                        }
                      >
                        <SelectTrigger className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GEMINI_THRESHOLDS.map((th) => (
                            <SelectItem key={th} value={th}>
                              {t(
                                `llmConfig.modelSpecific.safetySettings.thresholds.${th}`
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}
